import { useEffect, useState } from "react";
import { osFetch } from "./api";
import { useOS } from "./OSContext";
import { toast } from "sonner";
import { Bell, Loader2, Send, CheckCircle2, XCircle, Mail } from "lucide-react";

export default function NotificationSettings() {
  const { can } = useOS();
  const [channels, setChannels] = useState([]);
  const [settings, setSettings] = useState(null);
  const [log, setLog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () => Promise.all([
    osFetch("/notify/channels").then(setChannels),
    osFetch("/notify/settings").then(setSettings),
    osFetch("/notify/log").then(setLog),
  ]).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try { setSettings(await osFetch("/notify/settings", { method: "PUT", body: { recipient_email: settings.recipient_email, daily_brief_enabled: settings.daily_brief_enabled } })); toast.success("Saved"); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const test = async () => {
    setTesting(true);
    try { const r = await osFetch("/notify/test", { method: "POST" }); toast[r.status === "sent" ? "success" : "error"](`Test ${r.status}${r.channel ? ` via ${r.channel}` : ""}`); load(); }
    catch (e) { toast.error(e.message); } finally { setTesting(false); }
  };

  if (!settings) return <div className="flex items-center gap-2 text-slate-400 py-20"><Loader2 className="animate-spin" /> Loading…</div>;

  return (
    <div className="max-w-3xl" data-testid="os-notifications">
      <p className="font-mono text-[11px] uppercase tracking-widest text-[#00F0FF] mb-1 flex items-center gap-2"><Bell size={13} /> Notifications</p>
      <h1 className="font-display font-extrabold text-3xl">Delivery & Daily Brief</h1>
      <p className="text-slate-500 mt-1 text-sm">ArcaOS delivers the CEO Morning Brief over the best available channel. More channels unlock as they're connected.</p>

      <div className="os-glass rounded-2xl p-5 mt-6">
        <h2 className="font-display font-bold text-lg mb-4">Settings</h2>
        <label className="block mb-4">
          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Recipient email</span>
          <div className="flex items-center gap-2 mt-1"><Mail size={15} className="text-slate-500" /><input className="os-input" value={settings.recipient_email || ""} onChange={(e) => setSettings({ ...settings, recipient_email: e.target.value })} placeholder="you@company.com" data-testid="notify-recipient" /></div>
        </label>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={!!settings.daily_brief_enabled} onChange={(e) => setSettings({ ...settings, daily_brief_enabled: e.target.checked })} className="w-4 h-4 accent-[#0055FF]" data-testid="notify-daily-toggle" />
          <span className="text-sm text-slate-300">Email me the CEO Morning Brief every morning</span>
        </label>
        <div className="flex gap-2 mt-5">
          {can("update") && <button onClick={save} disabled={saving} className="bg-[#0055FF] text-white px-4 py-2.5 rounded-xl font-semibold text-sm disabled:opacity-50" data-testid="notify-save">{saving ? "Saving…" : "Save"}</button>}
          {can("update") && <button onClick={test} disabled={testing} className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 rounded-xl text-sm hover:border-white/30" data-testid="notify-test">{testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test</button>}
        </div>
      </div>

      <div className="os-glass rounded-2xl p-5 mt-4">
        <h2 className="font-display font-bold text-lg mb-3">Channels</h2>
        <div className="grid sm:grid-cols-2 gap-2">
          {channels.map((c) => (
            <div key={c.key} className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/30 px-3 py-2.5" data-testid={`notify-channel-${c.key}`}>
              {c.available ? <CheckCircle2 size={15} className="text-emerald-400" /> : <XCircle size={15} className="text-slate-600" />}
              <span className={`text-sm ${c.available ? "text-slate-200" : "text-slate-500"}`}>{c.label}</span>
              {!c.available && <span className="ml-auto text-[9px] font-mono uppercase tracking-widest text-slate-600">soon</span>}
            </div>
          ))}
        </div>
      </div>

      <div className="os-glass rounded-2xl p-5 mt-4">
        <h2 className="font-display font-bold text-lg mb-3">Recent deliveries</h2>
        {log.length === 0 ? <p className="text-slate-600 text-sm">No notifications sent yet.</p> : (
          <div className="divide-y divide-white/5">
            {log.map((n) => (
              <div key={n.id} className="py-2.5 flex items-center gap-3 text-sm" data-testid={`notify-log-${n.id}`}>
                <span className={`text-[10px] font-mono uppercase tracking-widest ${n.status === "sent" ? "text-emerald-400" : n.status === "failed" ? "text-rose-400" : "text-slate-500"}`}>{n.status}</span>
                <span className="text-slate-400">{n.type}</span>
                <span className="text-slate-600 text-xs ml-auto">{n.channel || "—"} · {new Date(n.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
